import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from "react-i18next";
import { Box, Typography } from "@material-ui/core";
import { Field, Formik } from "formik";
import AppMultipleSelect from "../../../../components/Input/AppMultipleSelect";
import AppButton from "../../../../components/CustomButtons/AppButton";
import AppColor from "../../../../utilities/AppColor";
import { Clear } from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";
import { SYSTEM_ROLE } from "../../../../config/AppConfig";
import { useDispatch, useSelector } from "react-redux";
import AppDialog from "../../../../components/Dialog/AppDialog";
import AppCheckbox from "../../../../components/Input/AppCheckbox";
import { fetchAccreditation, fetchLanguages, fetchSpecialties, fetchSupports } from "../../../../store/slices/worker";
import CustomDateTimePicker from "../../../../components/Input/CustomDateTimePicker";
import { getAuthInfo } from "../../../../store/slices/auth";
import { useHistory } from 'react-router-dom';
import AppUtil from 'utilities/AppUtil';

const useStyles = makeStyles(() => ({
    filterTitle: {
        whiteSpace: 'nowrap',
        fontSize: '16px',
        color: '#000',
        marginRight: '20px'
    },
    selectorContainer: {
        margin: 0,
        marginRight: '10px',
        minWidth: '250px',
        maxWidth: '300px',
        marginBottom: '10px',
        "& .dropdown-heading-value": {
            marginLeft: '15px'
        }
    },
    modalTitleComponent: {
        backgroundColor: 'white',
        borderTopWidth: '4px',
        borderTopStyle: 'solid',
        borderTopColor: AppColor.mainColor
    },
    modalTitle: {
        backgroundColor: AppColor.mainColor,
    },
    modalCloseButton: {
        color: AppColor.red
    }
}))

const SearchFilter = props => {
    const {
        onSubmit,
        queries
    } = props;
    const { t } = useTranslation();
    const classes = useStyles();
    const dispatch = useDispatch();
    const history = useHistory();
    const isMobile = AppUtil.mobileAndTabletCheck();
    const user = useSelector(getAuthInfo);
    const GENDERS = [
        {
            label: t('label.male'),
            value: 'MALE'
        },
        {
            label: t('label.female'),
            value: 'FEMALE'
        },
        {
            label: t('label.other'),
            value: 'OTHER'
        }
    ];

    const initializeForm = {
        accreditations: [],
        specialties: [],
        supports: [],
        gender: [],
        datetime: '',
        languages: []
    };

    const [specialties, setSpecialties] = useState([]);
    const [accreditations, setAccreditations] = useState([]);
    const [supports, setSupports] = useState([]);
    const [languages, setLanguages] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [initializeData, setInitializeData] = useState(initializeForm);
    const isHospitalUser = user.role === SYSTEM_ROLE.HOSPITAL_ADMIN || user.role === SYSTEM_ROLE.HOSPITAL_OTHER
    const isProvider = user.role === SYSTEM_ROLE.COMPANY || user.role === SYSTEM_ROLE.PARTICIPANT || user.role === SYSTEM_ROLE.COORDINATOR_ADMIN || user.role === SYSTEM_ROLE.COORDINATOR_OTHER;
    const _isNurse = (supports) => {
        return supports.some(sup => sup.isSupportForNurse);
    }
    const _loadLanguages = async () => {
        const { payload } = await dispatch(fetchLanguages());
        if (payload) {
            if (payload) {
                const languages = {};
                payload.forEach(lang => {
                    languages[lang.language.toLowerCase()] = false;
                })
                setLanguages(payload);
            }
        }
        return payload;
    }
    const _loadSpecialties = async () => {
        const { payload } = await dispatch(fetchSpecialties());
        if (payload) {
            setSpecialties(payload);
        }
        return payload;
    }
    const _loadAccreditations = async () => {
        const { payload } = await dispatch(fetchAccreditation({}));
        if (payload) {
            setAccreditations(payload.filter(e => e.workerType === SYSTEM_ROLE.NURSE));
        }
        return payload;
    }
    const _loadSupports = async () => {
        const { payload } = await dispatch(fetchSupports());
        if (payload) {
            setSupports(payload);
        }
        return payload;
    }
    const _loadOptions = () => {
        Promise.all([
            _loadLanguages(),
            _loadSpecialties(),
            _loadAccreditations(),
            _loadSupports()
        ]).then((data) => {
            // Supports
            const initSupports = [];
            queries.supportIds && queries.supportIds.forEach((id) => {
                const support = data[3].find(e => e.id === id);
                if (support) {
                    initSupports.push(support);
                }
            })
            // Accreditations
            const initAccreditations = [];
            queries.accreditationIds && queries.accreditationIds.forEach((id) => {
                const acc = data[2].find(e => e.id === id);
                if (acc) {
                    initAccreditations.push(acc);
                }
            })
            // Specialties
            let initSpecialties = [];
            let initGenders = [];
            if (isHospitalUser) {
                queries.specialtyIds && queries.specialtyIds.forEach((id) => {
                    const specialty = data[1].find(e => e.id === id);
                    if (specialty) {
                        initSpecialties.push(specialty);
                    }
                })
                initSpecialties = initSpecialties.map(e => ({ ...e, value: e.id, label: e.name }));
                initGenders = queries.genders;
            } else {
                initSpecialties = queries.specialtyIds;
                queries.genders && queries.genders.forEach((gender) => {
                    const gen = GENDERS.find(e => e.value === gender);
                    if (gen) {
                        initGenders.push(gen);
                    }
                })
            }

            setInitializeData(history.location.state ? {
                ...initializeData,
                languages: queries.languageIds,
                supports: initSupports.map(e => ({ ...e, value: e.id, label: e.name })),
                accreditations: initAccreditations.map(e => ({ ...e, value: e.id, label: e.name })),
                specialties: initSpecialties,
                gender: initGenders,
                ...queries.dateTime && {
                    datetime: new Date(queries.dateTime)
                }
            } : { ...initializeForm })
        })
    }

    useEffect(() => {
        _loadOptions();
    }, [])
    return (
        <Box display="flex" flexDirection={isMobile ? 'column' : 'row'} alignItems={`${isMobile ? 'flex-start' : 'center'}`} mt={3}>
            <Typography className={classes.filterTitle} variant="body2">
                {t('label.filter_by')}:
            </Typography>
            <Formik
                initialValues={initializeData}
                onSubmit={onSubmit}
                enableReinitialize
            >
                {({ resetForm, handleSubmit, values, handleChange }) => (
                    <Box display="flex" alignItems="center" flexWrap="wrap">
                        {isHospitalUser && (
                            <>
                                <Field
                                    component={AppMultipleSelect}
                                    name="accreditations"
                                    placeholder={t('label.accreditation')}
                                    className={classes.selectorContainer}
                                    options={accreditations.map(e => ({ ...e, value: e.id, label: e.name }))}
                                    disableSearch
                                    selectChange={() => {
                                        handleSubmit();
                                    }}
                                />
                                <Field
                                    component={AppMultipleSelect}
                                    name="specialties"
                                    placeholder={t('label.specialty')}
                                    className={classes.selectorContainer}
                                    options={specialties.map(e => ({ ...e, value: e.id, label: e.name }))}
                                    disableSearch
                                    selectChange={() => {
                                        handleSubmit();
                                    }}
                                />
                            </>
                        )}
                        {isProvider && (
                            <>
                                <Field
                                    component={AppMultipleSelect}
                                    name="gender"
                                    placeholder={t('label.gender')}
                                    className={classes.selectorContainer}
                                    options={GENDERS}
                                    disableSearch
                                    selectChange={() => {
                                        handleSubmit();
                                    }}
                                />
                                <Field
                                    component={AppMultipleSelect}
                                    name="supports"
                                    placeholder={t('label.support')}
                                    className={classes.selectorContainer}
                                    options={supports.map(e => ({ ...e, value: e.id, label: e.name }))}
                                    disableSearch
                                    selectChange={() => {
                                        handleSubmit();
                                    }}
                                />
                            </>
                        )}
                        <AppButton
                            label={t('label.clear_filter')}
                            buttonColor={AppColor.black}
                            onClick={() => {
                                resetForm({ values: initializeForm });
                                handleSubmit();
                            }}
                            mode="outlined"
                            style={{
                                borderWidth: '1px',
                                marginRight: 10,
                                fontWeight: 'normal',
                                boxShadow: 'none',
                                borderRadius: '13px',
                                marginBottom: '10px',
                                paddingTop: 0,
                                paddingBottom: 0,
                                fontSize: '13px',
                                minHeight: 29
                            }}
                            endIcon={<Clear />}
                        />
                        <AppButton
                            label={t('label.more_filters')}
                            onClick={() => setShowModal(true)}
                            buttonColor={AppColor.pink}
                            mode="outlined"
                            style={{
                                borderWidth: '1px',
                                marginRight: 10,
                                fontWeight: 'normal',
                                boxShadow: 'none',
                                borderRadius: '13px',
                                marginBottom: '10px',
                                paddingTop: 0,
                                paddingBottom: 0,
                                fontSize: '13px',
                                minHeight: 29
                            }}
                            endIcon={(
                                <>
                                    <i className="fas fa-filter" style={{ fontSize: '14px' }} />
                                </>
                            )}
                        />
                        <AppDialog
                            open={showModal}
                            title={t('title.more_filters')}
                            onClose={() => setShowModal(false)}
                            titleComponentClass={classes.modalTitleComponent}
                            closeTitleClass={classes.modalTitle}
                            closeIconClass={classes.modalCloseButton}
                        >
                            <Box>
                                {isProvider && _isNurse(values.supports) && (
                                    <>
                                        <Typography variant="h5">
                                            {t('label.specialty')} ({t('label.nurse')})
                                        </Typography>
                                        <Box pl={3}>
                                            <Field
                                                component={AppCheckbox}
                                                name="specialties"
                                                label={t('label.all')}
                                                className={'w-100'}
                                                field={{
                                                    value: values.specialties?.length === specialties?.length,
                                                    onChange: () => { }
                                                }}
                                                inputChange={(value) => {
                                                    if (value) {
                                                        handleChange({
                                                            target: {
                                                                name: 'specialties',
                                                                value: specialties.map(lang => lang.id)
                                                            }
                                                        })
                                                    } else {
                                                        handleChange({
                                                            target: {
                                                                name: 'specialties',
                                                                value: []
                                                            }
                                                        })
                                                    }
                                                    handleSubmit();
                                                }}
                                            />
                                            {specialties.map((specialty) => (
                                                <Field
                                                    key={specialty.id}
                                                    component={AppCheckbox}
                                                    name={`specialties`}
                                                    label={specialty.name}
                                                    className={'w-100'}
                                                    field={{
                                                        value: values.specialties?.includes(specialty.id),
                                                        onChange: () => { }
                                                    }}
                                                    inputChange={(e) => {
                                                        let newValue = [...values.specialties];
                                                        if (e && !newValue.includes(specialty.id)) {
                                                            newValue.push(specialty.id);
                                                        } else {
                                                            newValue = newValue.filter(e => e !== specialty.id);
                                                        }
                                                        handleChange({
                                                            target: {
                                                                name: 'specialties',
                                                                value: newValue
                                                            }
                                                        })
                                                        handleSubmit();
                                                    }}
                                                />
                                            ))}
                                        </Box>
                                    </>
                                )}
                                {isHospitalUser && (
                                    <>
                                        <Typography variant="h5">
                                            {t('label.gender')}
                                        </Typography>
                                        <Box pl={3}>
                                            <Field
                                                component={AppCheckbox}
                                                name="gender"
                                                label={t('label.all')}
                                                className={'w-100'}
                                                field={{
                                                    value: values.gender?.length === 3,
                                                    onChange: () => { }
                                                }}
                                                inputChange={(e) => {
                                                    handleChange({
                                                        target: {
                                                            name: `gender`,
                                                            value: e ? ['MALE', 'FEMALE', 'OTHER'] : []
                                                        }
                                                    });
                                                    handleSubmit();
                                                }}
                                            />
                                            <Field
                                                component={AppCheckbox}
                                                name="gender"
                                                label={t('label.male')}
                                                className={'w-100'}
                                                field={{
                                                    value: values.gender?.includes('MALE'),
                                                    onChange: () => { }
                                                }}
                                                inputChange={(e) => {
                                                    let newValue = [...values.gender];
                                                    if (e && !newValue.includes('MALE')) {
                                                        newValue.push('MALE');
                                                    } else {
                                                        newValue = newValue.filter(e => e !== 'MALE');
                                                    }
                                                    handleChange({
                                                        target: {
                                                            name: 'gender',
                                                            value: newValue
                                                        }
                                                    });
                                                    handleSubmit();
                                                }}
                                            />
                                            <Field
                                                component={AppCheckbox}
                                                name="gender"
                                                label={t('label.female')}
                                                className={'w-100'}
                                                field={{
                                                    value: values.gender?.includes('FEMALE'),
                                                    onChange: () => { }
                                                }}
                                                inputChange={(e) => {
                                                    let newValue = [...values.gender];
                                                    if (e && !newValue.includes('FEMALE')) {
                                                        newValue.push('FEMALE');
                                                    } else {
                                                        newValue = newValue.filter(e => e !== 'FEMALE');
                                                    }
                                                    handleChange({
                                                        target: {
                                                            name: 'gender',
                                                            value: newValue
                                                        }
                                                    });
                                                    handleSubmit();
                                                }}
                                            />
                                            <Field
                                                component={AppCheckbox}
                                                name="gender"
                                                label={t('label.other')}
                                                className={'w-100'}
                                                field={{
                                                    value: values.gender?.includes('OTHER'),
                                                    onChange: () => { }
                                                }}
                                                inputChange={(e) => {
                                                    let newValue = [...values.gender];
                                                    if (e && !newValue.includes('OTHER')) {
                                                        newValue.push('OTHER');
                                                    } else {
                                                        newValue = newValue.filter(e => e !== 'OTHER');
                                                    }
                                                    handleChange({
                                                        target: {
                                                            name: 'gender',
                                                            value: newValue
                                                        }
                                                    });
                                                    handleSubmit();
                                                }}
                                            />
                                        </Box>
                                    </>
                                )}
                                <Typography variant="h5">
                                    {t('label.date_and_time')}
                                </Typography>
                                <Box pl={3} pt={2}>
                                    <Field
                                        component={CustomDateTimePicker}
                                        name="datetime"
                                        InputProps={{
                                            fullWidth: true
                                        }}
                                        onChange={() => {
                                            handleSubmit();
                                        }}
                                    />
                                </Box>
                                <Typography variant="h5">
                                    {t('label.languages')}
                                </Typography>
                                <Box pl={3}>
                                    <Field
                                        component={AppCheckbox}
                                        name="languages"
                                        label={t('label.all')}
                                        className={'w-100'}
                                        field={{
                                            value: values.languages?.length === languages?.length,
                                            onChange: () => { }
                                        }}
                                        inputChange={(value) => {
                                            if (value) {
                                                handleChange({
                                                    target: {
                                                        name: 'languages',
                                                        value: languages.map(lang => lang.id)
                                                    }
                                                })
                                            } else {
                                                handleChange({
                                                    target: {
                                                        name: 'languages',
                                                        value: []
                                                    }
                                                })
                                            }
                                            handleSubmit();
                                        }}
                                    />
                                    {languages.map((lang) => (
                                        <Field
                                            key={lang.id}
                                            component={AppCheckbox}
                                            name={`languages`}
                                            label={lang.language}
                                            className={'w-100'}
                                            field={{
                                                value: values.languages?.includes(lang.id),
                                                onChange: () => { }
                                            }}
                                            inputChange={(e) => {
                                                let newValue = [...values.languages];
                                                if (e && !newValue.includes(lang.id)) {
                                                    newValue.push(lang.id);
                                                } else {
                                                    newValue = newValue.filter(e => e !== lang.id);
                                                }
                                                handleChange({
                                                    target: {
                                                        name: 'languages',
                                                        value: newValue
                                                    }
                                                })
                                                handleSubmit();
                                            }}
                                        />
                                    ))}
                                </Box>
                            </Box>
                        </AppDialog>
                    </Box>
                )}
            </Formik>
        </Box>
    )
}

SearchFilter.propTypes = {
    onSubmit: PropTypes.func,
    removeSearchWorkerQueries: PropTypes.func,
    queries: PropTypes.object
}

export default SearchFilter;
